// src/pages/employee/TeamEOD.jsx

import {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Download,
    CalendarDays,
    CheckCircle2,
    Flame,
    Rocket,
    Search,
    Users,
    ChevronDown,
    ChevronUp,
    ClipboardList,
} from "lucide-react";

import api from "../../api/axios";

const vibeMap = {
    crushing_it: {
        emoji: "🚀",
        label: "Crushing",
    },

    solid_day: {
        emoji: "💪",
        label: "Solid",
    },

    getting_there: {
        emoji: "😐",
        label: "Average",
    },

    rough_day: {
        emoji: "🌧️",
        label: "Rough",
    },

    on_fire: {
        emoji: "🔥",
        label: "On Fire",
    },
};

const formatRoleLabel = (role = "") => {
    const value = String(role || "").trim();
    if (!value) return "";
    return value
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
};

const TeamEOD = () => {

    const [employees, setEmployees] =
        useState([]);

    const [reports, setReports] =
        useState([]);

    const [
        selectedEmployee,
        setSelectedEmployee,
    ] = useState(null);

    const [
        expandedRow,
        setExpandedRow,
    ] = useState(null);

    const [loading, setLoading] =
        useState(true);

    const [search, setSearch] =
        useState("");

    const [selectedTeam, setSelectedTeam] =
        useState("");

    const [expandedTeams, setExpandedTeams] = useState({});

    // ================= FETCH =================
    useEffect(() => {

        const fetchData =
            async () => {

                try {

                    const [
                        employeesRes,
                        reportsRes,
                    ] =
                        await Promise.all([
                            api.get(
                                "/api/employee/team/members",
                            ),

                            api.get(
                                "/api/employee/team/eod",
                            ),
                        ]);

                    setEmployees(
                        employeesRes.data ||
                        [],
                    );

                    setReports(
                        reportsRes.data ||
                        [],
                    );

                } catch (err) {

                    console.error(
                        "Failed to load team EOD data:",
                        err,
                    );

                } finally {

                    setLoading(false);
                }
            };

        fetchData();

    }, []);

    const teamGroups = useMemo(() => {
        const groups = new Map();
        employees.forEach((employee) => {
            const team = String(employee?.team || "Unassigned").trim() || "Unassigned";
            if (!groups.has(team)) groups.set(team, []);
            groups.get(team).push(employee);
        });
        return [...groups.entries()]
            .map(([team, members]) => ({
                team,
                members: [...members].sort((left, right) => String(left.name || "").localeCompare(String(right.name || ""))),
            }))
            .sort((left, right) => left.team.localeCompare(right.team));
    }, [employees]);

    const visibleTeamGroups = useMemo(() => (
        selectedTeam
            ? teamGroups.filter(({ team }) => team === selectedTeam)
            : []
    ), [selectedTeam, teamGroups]);

    const handleTeamChange = (team) => {
        setSelectedTeam(team);
        setSelectedEmployee(null);
        setExpandedRow(null);
    };

    const toggleEmployee = (member) => {
        const isSelected = selectedEmployee?._id === member._id;
        setSelectedEmployee(isSelected ? null : member);
        setExpandedRow(null);
    };

    // ================= FILTERED =================
    const filtered =
        useMemo(() => {

            return reports
                .filter(
                    (r) =>
                        r.employee
                            ?._id ===
                        selectedEmployee
                            ?._id,
                )
                .filter(
                    (r) =>
                        r.workSummary
                            ?.toLowerCase()
                            .includes(
                                search.toLowerCase(),
                            ),
                )
                .sort(
                    (
                        a,
                        b,
                    ) =>
                        new Date(
                            b.date,
                        ) -
                        new Date(
                            a.date,
                        ),
                );

        }, [
            reports,
            selectedEmployee,
            search,
        ]);

    // ================= STATS =================
    const stats =
        useMemo(() => {

            return {

                total:
                    filtered.length,

                submitted:
                    filtered.filter(
                        (
                            r,
                        ) =>
                            r.status ===
                            "submitted",
                    ).length,

                fire:
                    filtered.filter(
                        (
                            r,
                        ) =>
                            r.dayVibe ===
                            "on_fire",
                    ).length,
            };

        }, [filtered]);

    const todayKey = new Date().toISOString().split("T")[0];
    const todaySubmittedIds = useMemo(() => new Set(
        reports
            .filter((report) => String(report.date || "").slice(0, 10) === todayKey && report.status === "submitted")
            .map((report) => String(report.employee?._id || report.employee)),
    ), [reports, todayKey]);
    const missingToday = useMemo(() => employees.filter((employee) => !todaySubmittedIds.has(String(employee._id))), [employees, todaySubmittedIds]);

    // ================= EXPORT =================
    const exportCSV =
        () => {

            const rows = [
                [
                    "Employee",
                    "Date",
                    "Summary",
                    "Status",
                    "TomorrowPlan",
                    "Vibe",
                ],

                ...filtered.map(
                    (
                        r,
                    ) => [
                            r.employee
                                ?.name,

                            r.date,

                            r.workSummary,

                            r.status,

                            r.tomorrowPlan,

                            r.dayVibe,
                        ],
                ),
            ];

            const csv =
                "data:text/csv;charset=utf-8," +
                rows
                    .map(
                        (
                            r,
                        ) =>
                            r.join(
                                ",",
                            ),
                    )
                    .join("\n");

            const link =
                document.createElement(
                    "a",
                );

            link.href =
                encodeURI(csv);

            link.download =
                `team_eod_${Date.now()}.csv`;

            link.click();
        };

    // ================= LOADING =================
    if (loading) {

        return (
            <div
                className="
                min-h-screen

                bg-[var(--bg-primary)]
                text-[var(--text-primary)]

                flex
                items-center
                justify-center
            "
            >
                Loading Team EOD...
            </div>
        );
    }

    return (
        <div
            className="
            team-eod-page
            min-h-screen

            bg-[var(--bg-primary)]
            text-[var(--text-primary)]

            transition-all
        "
        >
            <div
                className="
                team-eod-shell
                max-w-7xl
                mx-auto

                p-6

                space-y-6
            "
            >

                {/* ================= HERO ================= */}
                <div
                    className="
                    team-eod-hero
                    bg-[var(--card-bg)]

                    border
                    border-[var(--border-color)]

                    shadow-[var(--shadow-soft)]

                    rounded-3xl

                    p-8
                "
                >

                    <div
                        className="
                        team-eod-hero-inner
                        flex
                        items-center
                        justify-between

                        gap-6
                    "
                    >

                        <div>

                            <h1
                                className="
                                team-eod-title
                                text-4xl
                                font-bold
                            "
                            >
                                Team EOD Reports
                            </h1>

                            <p
                                className="
                                team-eod-subtitle
                                mt-3

                                text-lg

                                text-[var(--text-secondary)]
                            "
                            >
                                Daily execution,
                                team performance
                                insights
                            </p>

                        </div>

                        <div
                            className="
                            hidden
                            md:flex

                            w-20
                            h-20

                            rounded-3xl

                            bg-[var(--accent-primary)]

                            items-center
                            justify-center
                        "
                        >

                            <ClipboardList
                                size={34}
                                className="
                                text-black
                            "
                            />

                        </div>

                    </div>

                </div>

                {/* ================= STATS ================= */}
                <section className="team-eod-missing-card bg-[var(--card-bg)] border border-[var(--border-color)] shadow-[var(--shadow-soft)] rounded-3xl p-6 mb-6">
                    <div className="team-eod-missing-head flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-wide text-[var(--accent-primary)]">Missing EOD tracker</p>
                            <h2 className="text-2xl font-bold">Today submissions</h2>
                        </div>
                        <span className="text-sm text-[var(--text-secondary)]">{todaySubmittedIds.size} submitted · {missingToday.length} missing</span>
                    </div>
                    <div className="team-eod-missing-list mt-4 flex flex-wrap gap-2">
                        {missingToday.length === 0 ? (
                            <span className="rounded-full bg-emerald-500/10 px-3 py-1.5 text-xs font-bold text-emerald-600">Everyone submitted today</span>
                        ) : missingToday.map((member) => (
                            <span key={member._id} className="rounded-full bg-amber-500/10 px-3 py-1.5 text-xs font-bold text-amber-600">{member.name}</span>
                        ))}
                    </div>
                </section>

                <div
                    className="
                    team-eod-stat-grid
                    grid
                    grid-cols-1
                    md:grid-cols-3

                    gap-5
                "
                >

                    <StatCard
                        icon={
                            <CalendarDays />
                        }
                        title="Reports"
                        value={
                            stats.total
                        }
                    />

                    <StatCard
                        icon={
                            <CheckCircle2 />
                        }
                        title="Submitted"
                        value={
                            stats.submitted
                        }
                    />

                    <StatCard
                        icon={
                            <Flame />
                        }
                        title="On Fire"
                        value={
                            stats.fire
                        }
                    />

                </div>

                {/* ================= TOP BAR ================= */}
                <div
                    className="
                    flex
                    flex-col
                    lg:flex-row

                    gap-5
                "
                >
                    <div
                        className="
                        w-full
                        lg:w-[320px]

                        bg-[var(--card-bg)]

                        border
                        border-[var(--border-color)]

                        shadow-sm
                        flex
                        flex-col
                        h-fit

                        rounded-2xl
                    "
                    >

                        <div className="border-b border-[var(--border-color)] p-4 bg-gradient-to-b from-[var(--bg-secondary)]/50 to-transparent">
                            <h3 className="text-xs font-black uppercase tracking-wider text-[var(--text-secondary)] mb-3 px-1">
                                Employees & Teams
                            </h3>
                            <div className="relative">
                                <Search
                                    size={16}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]"
                                />
                                <input
                                    placeholder="Search team..."
                                    className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] pl-10 pr-3 text-sm outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] transition-all duration-200"
                                />
                            </div>
                        </div>

                        <div className="p-3 space-y-2 flex-1">
                            {teamGroups.map(({ team, members }) => (
                                <div key={team} className="mb-2">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setExpandedTeams((prev) => ({
                                                ...prev,
                                                [team]: !prev[team],
                                            }))
                                        }
                                        className="flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-xs font-black uppercase tracking-wider text-[var(--text-secondary)] hover:bg-[var(--bg-primary)] hover:text-[var(--text-primary)] transition-all duration-200"
                                    >
                                        <span className="flex items-center gap-2">
                                            {expandedTeams[team] ? (
                                                <ChevronUp size={14} className="text-[var(--accent-primary)]" />
                                            ) : (
                                                <ChevronDown size={14} />
                                            )}
                                            {team}
                                        </span>
                                        <span className="rounded-full bg-[var(--border-color)] px-2.5 py-0.5 text-[10px] font-bold text-[var(--text-secondary)]">
                                            {members.length}
                                        </span>
                                    </button>

                                    {expandedTeams[team] && (
                                        <div className="mt-1 space-y-1 pl-2 overflow-hidden">
                                            {members.length ? (
                                                members.map((employee) => {
                                                    const isSelected = selectedEmployee?._id === employee._id;
                                                    const nameInitial = employee.name?.charAt(0)?.toUpperCase() || "E";
                                                    return (
                                                        <button
                                                            key={employee._id}
                                                            type="button"
                                                            onClick={() => toggleEmployee(employee)}
                                                            className={`w-full rounded-xl border p-2.5 text-left transition-all duration-200 ${
                                                                isSelected
                                                                    ? "border-violet-500 bg-gradient-to-r from-violet-500/10 to-indigo-500/5 text-[var(--accent-primary)] shadow-sm"
                                                                    : "border-transparent bg-transparent hover:bg-[var(--bg-primary)] hover:border-[var(--border-color)] text-[var(--text-primary)]"
                                                            }`}
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xs font-black overflow-hidden ${
                                                                    isSelected
                                                                        ? "bg-gradient-to-tr from-violet-600 to-indigo-600 text-white shadow-sm"
                                                                        : "bg-[var(--border-color)] text-[var(--text-secondary)]"
                                                                }`}>
                                                                    {nameInitial}
                                                                </div>
                                                                <div className="min-w-0 flex-1">
                                                                    <p className="truncate text-sm font-bold leading-tight">
                                                                        {employee.name}
                                                                    </p>
                                                                    <p className="truncate text-[10px] font-semibold text-[var(--text-secondary)] mt-0.5">
                                                                        {formatRoleLabel(employee.role) || employee.designation || team}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </button>
                                                    );
                                                })
                                            ) : (
                                                <p className="px-3 py-2 text-xs text-[var(--text-secondary)] italic">
                                                    No employees
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                    </div>

                    {/* REPORTS */}
                    <div
                        className="
                        flex-1
                        space-y-5
                    "
                    >

                        {/* FILTER */}
                        <div
                            className="
                            bg-[var(--card-bg)]

                            border
                            border-[var(--border-color)]

                            shadow-[var(--shadow-soft)]
                            rounded-3xl

                            p-5
                        "
                        >

                            <div className="team-eod-report-context">
                                <span>Viewing reports for</span>
                                <strong>{selectedEmployee?.name || "Select a team member"}</strong>
                                {selectedEmployee?.team && <em>{selectedEmployee.team}</em>}
                            </div>

                            <div
                                className="
                                flex
                                flex-col
                                md:flex-row

                                gap-4
                            "
                            >

                                {/* SEARCH */}
                                <div
                                    className="
                                    relative

                                    flex-1
                                "
                                >

                                    <Search
                                        size={18}
                                        className="
                                        absolute

                                        left-4
                                        top-1/2
                                        -translate-y-1/2

                                        text-[var(--text-secondary)]
                                    "
                                    />

                                    <input
                                        placeholder="Search reports..."
                                        value={
                                            search
                                        }
                                        onChange={(
                                            e,
                                        ) =>
                                            setSearch(
                                                e
                                                    .target
                                                    .value,
                                            )
                                        }
                                        className="
                                        w-full

                                        bg-[var(--bg-secondary)]

                                        border
                                        border-[var(--border-color)]

                                        pl-11
                                        pr-4
                                        py-3

                                        rounded-2xl

                                        outline-none
                                    "
                                    />

                                </div>

                                {/* EXPORT */}
                                <button
                                    onClick={
                                        exportCSV
                                    }
                                    className="
                                    inline-flex
                                    items-center
                                    justify-center

                                    gap-3

                                    bg-[var(--accent-primary)]
                                    hover:bg-[var(--accent-hover)]

                                    text-white

                                    px-5
                                    py-3

                                    rounded-2xl

                                    font-medium

                                    transition-all
                                "
                                >

                                    <Download
                                        size={18}
                                    />

                                    Export CSV

                                </button>

                            </div>

                        </div>

                        {/* REPORTS LIST */}
                        {filtered.length ===
                            0 ? (
                            <div
                                className="
                                bg-[var(--card-bg)]

                                border
                                border-[var(--border-color)]

                                shadow-[var(--shadow-soft)]

                                rounded-3xl

                                p-10

                                text-center
                            "
                            >

                                <Rocket
                                    size={42}
                                    className="
                                    mx-auto

                                    text-[var(--text-secondary)]

                                    mb-4
                                "
                                />

                                <p
                                    className="
                                    text-lg
                                    font-medium
                                "
                                >
                                    No reports found
                                </p>

                            </div>
                        ) : (
                            <div
                                className="
                                space-y-4
                            "
                            >

                                {filtered.map(
                                    (
                                        r,
                                    ) => {

                                        const isOpen =
                                            expandedRow ===
                                            r._id;

                                        return (
                                            <div
                                                key={
                                                    r._id
                                                }
                                                className="
                                                bg-[var(--card-bg)]

                                                border
                                                border-[var(--border-color)]

                                                shadow-[var(--shadow-soft)]

                                                rounded-3xl

                                                overflow-hidden
                                            "
                                            >

                                                {/* HEADER */}
                                                <button
                                                    onClick={() =>
                                                        setExpandedRow(
                                                            isOpen
                                                                ? null
                                                                : r._id,
                                                        )
                                                    }
                                                    className="
                                                    w-full

                                                    flex
                                                    flex-col
                                                    lg:flex-row

                                                    lg:items-center
                                                    lg:justify-between

                                                    gap-5

                                                    p-6

                                                    text-left
                                                "
                                                >

                                                    <div>

                                                        <div
                                                            className="
                                                            flex
                                                            items-center
                                                            gap-3
                                                            flex-wrap
                                                        "
                                                        >

                                                            <span
                                                                className="
                                                                text-sm

                                                                text-[var(--text-secondary)]
                                                            "
                                                            >
                                                                {
                                                                    r.date
                                                                }
                                                            </span>

                                                            <div
                                                                className={`
                                                                px-3
                                                                py-1.5

                                                                rounded-full

                                                                text-xs
                                                                text-white

                                                                ${r.status ===
                                                                        "submitted"
                                                                        ? "bg-green-600"
                                                                        : "bg-yellow-500 text-black"
                                                                    }
                                                            `}
                                                            >
                                                                {
                                                                    r.status
                                                                }
                                                            </div>

                                                            <div
                                                                className="
                                                                px-3
                                                                py-1.5

                                                                rounded-full

                                                                bg-[var(--bg-secondary)]

                                                                text-sm
                                                            "
                                                            >
                                                                {
                                                                    vibeMap[
                                                                        r.dayVibe
                                                                    ]
                                                                        ?.emoji
                                                                }{" "}
                                                                {
                                                                    vibeMap[
                                                                        r.dayVibe
                                                                    ]
                                                                        ?.label
                                                                }
                                                            </div>

                                                        </div>

                                                        <p
                                                            className="
                                                            mt-4

                                                            text-lg
                                                            font-medium

                                                            line-clamp-2
                                                        "
                                                        >
                                                            {
                                                                r.workSummary
                                                            }
                                                        </p>

                                                    </div>

                                                    <div
                                                        className="
                                                        flex
                                                        items-center
                                                        gap-3
                                                    "
                                                    >

                                                        {isOpen ? (
                                                            <ChevronUp />
                                                        ) : (
                                                            <ChevronDown />
                                                        )}

                                                    </div>

                                                </button>

                                                {/* EXPANDED */}
                                                {isOpen && (
                                                    <div
                                                        className="
                                                        border-t
                                                        border-[var(--border-color)]

                                                        p-6

                                                        grid
                                                        md:grid-cols-1

                                                        gap-5
                                                    "
                                                    >

                                                        <ExpandCard
                                                            title="Tomorrow Plan"
                                                            content={
                                                                r.tomorrowPlan ||
                                                                "No plans added"
                                                            }
                                                        />

                                                    </div>
                                                )}

                                            </div>
                                        );
                                    },
                                )}

                            </div>
                        )}

                    </div>

                </div>

            </div>

        </div>
    );
};

// ================= STAT =================
const StatCard = ({
    icon,
    title,
    value,
}) => {

    return (
        <div
            className="
            bg-[var(--card-bg)]

            border
            border-[var(--border-color)]

            shadow-[var(--shadow-soft)]

            rounded-3xl

            p-6
        "
        >

            <div
                className="
                flex
                items-center
                justify-between

                mb-5
            "
            >

                <div
                    className="
                    text-[var(--accent-primary)]
                "
                >
                    {icon}
                </div>

            </div>

            <p
                className="
                text-4xl
                font-bold
            "
            >
                {value}
            </p>

            <p
                className="
                mt-2

                text-[var(--text-secondary)]
            "
            >
                {title}
            </p>

        </div>
    );
};

// ================= EXPAND =================
const ExpandCard = ({
    title,
    content,
}) => {

    return (
        <div
            className="
            bg-[var(--bg-secondary)]

            border
            border-[var(--border-color)]

            rounded-3xl

            p-5
        "
        >

            <p
                className="
                text-sm
                font-medium

                text-[var(--text-secondary)]

                mb-3
            "
            >
                {title}
            </p>

            <p
                className="
                leading-7
            "
            >
                {content}
            </p>

        </div>
    );
};

export default TeamEOD;
